import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { getProfileDetails } from '../services/profile';

interface HeaderProps {
  username?: string;
}

interface ProfileDetailsExtended {
  id?: string;
  username?: string;
  email?: string;
  status?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  phone_number?: string;
  terms_accepted?: boolean;
  terms_accepted_at?: string;
  terms_status?: string;
  application_id?: string;
  sso_reference_id?: string;
  language_code?: string;
  created_at?: string;
  updated_at?: string;
  properties?: any;
  [key: string]: any;
}

const Header: React.FC<HeaderProps> = ({ username = 'User' }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [profileDetails, setProfileDetails] = useState<ProfileDetailsExtended | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'personal' | 'system' | 'activity' | 'properties'>('personal');
  const auth = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Fetch profile details when modal opens
  const fetchProfileDetails = async () => {
    if (auth.isAuthenticated && auth.user) {
      setIsLoadingProfile(true);
      setProfileDetails(null); // Reset previous data
      try {
        const details = await getProfileDetails(auth.user);
        if (details) {
          setProfileDetails(details as ProfileDetailsExtended);
        }
      } catch (error) {
        console.error('Error fetching profile details:', error);
        setProfileDetails(null);
      } finally {
        setIsLoadingProfile(false);
      }
    }
  };

  // Fetch profile details when modal opens
  useEffect(() => {
    if (showProfileModal) {
      fetchProfileDetails();
    }
  }, [showProfileModal]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownOpen]);
  
  // Display name for dropdown - uses OIDC data to avoid flashing
  const dropdownDisplayName = auth.user?.profile.name || username;
  
  // Display name for modal - uses profile details when available
  const modalDisplayName = profileDetails?.first_name 
    ? profileDetails?.last_name 
      ? `${profileDetails.first_name} ${profileDetails.last_name}`
      : profileDetails.first_name
    : profileDetails?.username || auth.user?.profile.name || username;
  
  const userEmail = profileDetails?.email || auth.user?.profile.email || '';
  const userStatus = profileDetails?.status || 'Unknown';
  
  // Initials for dropdown
  const dropdownInitials = dropdownDisplayName
    .split(' ')
    .map(n => n.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
    
  // Initials for modal
  const modalInitials = modalDisplayName
    .split(' ')
    .map(n => n.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
  
  // Format phone number for display
  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '-';
    // Remove any non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    // Format as needed
    if (cleaned.startsWith('55')) {
      return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
    }
    return phone;
  };


  // Handle logout button click
  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isLoggingOut) {
      return; // Prevent multiple clicks
    }
    
    setIsLoggingOut(true);
    setDropdownOpen(false); // Close dropdown
    
    // Navigate directly to logout page
    setTimeout(() => {
      navigate('/logout');
    }, 100); // Small delay to ensure state updates
  };

  return (
    <header className="bg-white shadow-sm px-6 py-3 flex items-center justify-between">
      <div className="flex items-center">
        <h1 className="text-xl font-semibold text-gray-800">Dashboard</h1>
      </div>
      
      <div className="flex items-center space-x-4">
        <div className="relative">
          <button
            className="text-gray-600 hover:text-gray-800 focus:outline-none"
            aria-label="Notifications"
          >
            <i className="fas fa-bell"></i>
          </button>
        </div>
        
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center space-x-2 focus:outline-none"
            aria-haspopup="true"
            aria-expanded={dropdownOpen}
          >
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
              {dropdownInitials || dropdownDisplayName.charAt(0)}
            </div>
            <span className="text-gray-700">{dropdownDisplayName}</span>
            <i className="fas fa-chevron-down text-gray-500"></i>
          </button>
          
          {dropdownOpen && (
            <div 
              className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900">{dropdownDisplayName}</p>
                <p className="text-xs text-gray-500 truncate">{auth.user?.profile.email || ''}</p>
              </div>
              
              <button
                onClick={() => {
                  setShowProfileModal(true);
                  setDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <i className="fas fa-user mr-2"></i>
                Profile
              </button>
              
              <button 
                type="button"
                onClick={() => {}}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <i className="fas fa-cog mr-2"></i>
                Settings
              </button>
              
              <div className="border-t border-gray-100"></div>
              
              <a
                href="/logout"
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
              >
                <i className="fas fa-sign-out-alt mr-2"></i>
                Logout
              </a>
            </div>
          )}
        </div>
      </div>
      
      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4 pt-8">
          <div className="relative mx-auto max-w-4xl w-full bg-white rounded-lg shadow-xl max-h-[calc(100vh-4rem)] overflow-hidden">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-xl font-semibold text-gray-900">Profile Details</h3>
              <button
                onClick={() => setShowProfileModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
            
            <div className="overflow-y-auto max-h-[calc(100vh-8rem)] p-6">
            
            {(() => {
              if (isLoadingProfile) {
                return (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                );
              } else if (profileDetails) {
                return (
              <div className="space-y-6">
                {/* Header Section */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
                  <div className="flex items-center space-x-6">
                    <div className="w-24 h-24 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-3xl shadow-lg">
                      {modalInitials || modalDisplayName.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-2xl font-bold text-gray-900">{modalDisplayName}</h4>
                      <p className="text-gray-600 mt-1">{userEmail}</p>
                      <div className="flex items-center space-x-4 mt-3">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          userStatus === 'ACTIVATED' ? 'bg-green-100 text-green-700' :
                          userStatus === 'BLOCKED' ? 'bg-red-100 text-red-700' :
                          userStatus === 'INACTIVATED' ? 'bg-gray-100 text-gray-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          <i className={`fas fa-circle text-xs mr-2 ${
                            userStatus === 'ACTIVATED' ? 'text-green-500' :
                            userStatus === 'BLOCKED' ? 'text-red-500' :
                            userStatus === 'INACTIVATED' ? 'text-gray-500' :
                            'text-yellow-500'
                          }`}></i>
                          {userStatus}
                        </span>
                        <span className="text-sm text-gray-500">
                          Member since {profileDetails.created_at ? new Date(profileDetails.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Tabs Navigation */}
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    <button
                      onClick={() => setActiveTab('personal')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'personal'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <i className="fas fa-user-circle mr-2"></i>
                      Personal Information
                    </button>
                    <button
                      onClick={() => setActiveTab('system')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'system'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <i className="fas fa-cog mr-2"></i>
                      System Information
                    </button>
                    <button
                      onClick={() => setActiveTab('activity')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'activity'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <i className="fas fa-clock mr-2"></i>
                      Activity Timeline
                    </button>
                    {profileDetails?.properties && Object.keys(profileDetails.properties).length > 0 && (
                      <button
                        onClick={() => setActiveTab('properties')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeTab === 'properties'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <i className="fas fa-code mr-2"></i>
                        Properties
                      </button>
                    )}
                  </nav>
                </div>
                
                {/* Tab Content */}
                <div className="mt-6">
                  {activeTab === 'personal' && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Username</label>
                      <p className="mt-1 text-gray-900">{profileDetails.username || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="mt-1 text-gray-900">{profileDetails.email || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">First Name</label>
                      <p className="mt-1 text-gray-900">{profileDetails.first_name || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Name</label>
                      <p className="mt-1 text-gray-900">{profileDetails.last_name || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                      <p className="mt-1 text-gray-900">{formatPhoneNumber(profileDetails.phone || profileDetails.phone_number || '')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Language</label>
                      <p className="mt-1 text-gray-900">{profileDetails.language_code || 'Not set'}</p>
                    </div>
                  </div>
                    </div>
                  )}
                  
                  {activeTab === 'system' && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">User ID</label>
                      <p className="mt-1 text-gray-900 font-mono text-sm bg-gray-50 p-2 rounded">{profileDetails.id || '-'}</p>
                    </div>
                    {profileDetails.application_id && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Application ID</label>
                        <p className="mt-1 text-gray-900 font-mono text-sm bg-gray-50 p-2 rounded">{profileDetails.application_id}</p>
                      </div>
                    )}
                    {profileDetails.sso_reference_id && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">SSO Reference ID</label>
                        <p className="mt-1 text-gray-900 font-mono text-sm bg-gray-50 p-2 rounded">{profileDetails.sso_reference_id}</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Terms & Conditions</label>
                      <p className="mt-1">
                        {profileDetails.terms_status === 'ACCEPTED' ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium bg-green-100 text-green-700">
                            <i className="fas fa-check-circle mr-1.5"></i>
                            Accepted
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium bg-yellow-100 text-yellow-700">
                            <i className="fas fa-exclamation-circle mr-1.5"></i>
                            {profileDetails.terms_status || 'Not Accepted'}
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Account Status</label>
                      <p className="mt-1">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium ${
                          userStatus === 'ACTIVATED' ? 'bg-green-100 text-green-700' :
                          userStatus === 'BLOCKED' ? 'bg-red-100 text-red-700' :
                          userStatus === 'INACTIVATED' ? 'bg-gray-100 text-gray-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          <i className="fas fa-circle text-xs mr-1.5"></i>
                          {userStatus}
                        </span>
                      </p>
                    </div>
                  </div>
                    </div>
                  )}
                  
                  {activeTab === 'activity' && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Account Created</label>
                      <p className="mt-1 text-gray-900">
                        {profileDetails.created_at ? (
                          <>
                            {new Date(profileDetails.created_at).toLocaleDateString('en-US', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                            <span className="block text-sm text-gray-500">
                              {new Date(profileDetails.created_at).toLocaleTimeString()}
                            </span>
                          </>
                        ) : '-'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                      <p className="mt-1 text-gray-900">
                        {profileDetails.updated_at ? (
                          <>
                            {new Date(profileDetails.updated_at).toLocaleDateString('en-US', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                            <span className="block text-sm text-gray-500">
                              {new Date(profileDetails.updated_at).toLocaleTimeString()}
                            </span>
                          </>
                        ) : '-'}
                      </p>
                    </div>
                  </div>
                    </div>
                  )}
                  
                  {activeTab === 'properties' && profileDetails?.properties && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <div className="space-y-4">
                        <h6 className="text-sm font-semibold text-gray-700 mb-4">Additional Properties</h6>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                            {JSON.stringify(profileDetails.properties, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
                );
              } else {
                return (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Failed to load profile details</p>
                  </div>
                );
              }
            })()}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;